import React from 'react';
import { DeleteButton, EditButton, GradientButton, OutlineButton, PrimaryButton } from '../../components/Buttons';
import { ThreeDots } from 'react-loader-spinner';
import { FileDown } from 'lucide-react';
import CustomInput from '../../components/CustomInput';

const InstallmentsTable = ({
    installments,
    subscription,
    editingRowIndex,
    newInstallment,
    handleInstallmentChange,
    getInputValueDate,
    formatDate,
    handleEditClick,
    handleDeleteClick,
    handleUpdateInstallment,
    cancelAddRow,
    handleSaveNewInstallment,
    handleCancelEdit,
    isLoadingOverlayVisible,
    addInstallmentRow,
    handleGenerateReceipt,
    handleDownloadReceipt,
    handleGenerateInvoice,
    handleDownloadInvoice,
    previewMode = false, // added property
}) => {

    // Payment can't be received in the future — cap the received-date picker at today.
    const today = new Date().toISOString().split('T')[0];

    let TABLE_HEAD = [];
    if (previewMode) {
        TABLE_HEAD = ["#", "Projected Amount", "Projected Date", "Actions"];

    } else {
        TABLE_HEAD = ["#", "Projected Amount", "Received Amount", "Projected Date", "Received Date", "Invoice", "Receipt"];
    }

    // A document cell that toggles Generate -> Download once the doc exists (no regeneration).
    const renderDocCell = (installment, kind) => {
        const url = kind === 'invoice' ? installment.invoiceUrl : installment.receiptUrl;
        const onGenerate = kind === 'invoice' ? handleGenerateInvoice : handleGenerateReceipt;
        const onDownload = kind === 'invoice' ? handleDownloadInvoice : handleDownloadReceipt;
        // Receipts require recorded payment; invoices can be generated any time.
        const needsPayment =
            kind === 'receipt' &&
            (installment.receivedAmount == null || installment.receivedAmount === '' || !installment.receivedDate);
        return (
            <td className="p-2 border border-gray-300">
                {url ? (
                    <OutlineButton
                        size="sm"
                        className="w-28"
                        onClick={() => onDownload && onDownload(installment)}
                        disabled={isLoadingOverlayVisible}
                    >
                        <span className="inline-flex items-center gap-1.5"><FileDown className="h-4 w-4" /> Download</span>
                    </OutlineButton>
                ) : (
                    <GradientButton
                        onClick={() => onGenerate && onGenerate(installment)}
                        className={"w-28"}
                        disabled={
                            isLoadingOverlayVisible ||
                            editingRowIndex !== null ||
                            !installment.subscriptionCustomerInstallmentId ||
                            needsPayment
                        }
                    >
                        Generate
                    </GradientButton>
                )}
            </td>
        );
    };

    const getTableHead = () => {
        return TABLE_HEAD.map((head) => (
            <th key={head} className="border border-gray-300 bg-blue-gray-50 p-4">
                <span className="font-normal leading-none opacity-70">
                    {head}
                </span>
            </th>
        ));
    }
    const renderActions = (installment, index, isEditing) => {

        return (
            <td className="p-2 border border-gray-300">
                <div className='flex gap-2 items-center'>
                    {isEditing ? (
                        <>
                            <PrimaryButton size="sm" className="w-16" onClick={handleUpdateInstallment} disabled={isLoadingOverlayVisible}>
                                {isLoadingOverlayVisible ? <ThreeDots height="18" width="18" color="#fff" /> : 'Save'}
                            </PrimaryButton>
                            <OutlineButton size="sm" className="w-16" onClick={() => handleCancelEdit(index)} disabled={isLoadingOverlayVisible}>
                                Cancel
                            </OutlineButton>
                        </>
                    ) : (
                        <>
                            {/* Once a receipt is generated the installment is final — lock editing.
                                Deletion is blocked once any invoice/receipt has been generated. */}
                            <EditButton
                                size="sm"
                                onClick={() => handleEditClick(index)}
                                disabled={editingRowIndex !== null || isLoadingOverlayVisible || !!installment.receiptUrl}
                            >
                                Edit
                            </EditButton>
                            <DeleteButton
                                size="sm"
                                onClick={() => handleDeleteClick(installment.subscriptionCustomerInstallmentId, index)}
                                disabled={
                                    editingRowIndex !== null ||
                                    isLoadingOverlayVisible ||
                                    !!installment.invoiceUrl ||
                                    !!installment.receiptUrl
                                }
                                className='p-2'
                            >
                                Delete
                            </DeleteButton>
                        </>
                    )}
                </div>
            </td>
        )

    }

    const renderTableData = (installment, index, isEditing) => {
        return (
            <>
                <td className="p-2 border border-gray-300">{index + 1}</td>
                
                <td className="p-2 border border-gray-300">
                    {isEditing ? (
                        <CustomInput
                            type="number" step="0.01" size="sm"
                            value={installment.projectedAmount ?? ''}
                            onChange={(e) => handleInstallmentChange(index, 'projectedAmount', e.target.value)}
                        />
                    ) : (
                        installment.projectedAmount != null ? `₹${installment.projectedAmount}` : '-'
                    )}
                </td>
                {!previewMode && (<td className="p-2 border border-gray-300">
                    {isEditing ? (
                        <CustomInput
                            type="number" step="0.01" size="sm"
                            value={installment.receivedAmount ?? ''}
                            onChange={(e) => handleInstallmentChange(index, 'receivedAmount', e.target.value)}
                        />
                    ) : (
                        installment.receivedAmount != null ? `₹${installment.receivedAmount}` : '-'
                    )}
                </td>)}

                <td className="p-2 border border-gray-300">
                    {isEditing ? (
                        <CustomInput
                            type="date" size="sm"
                            value={getInputValueDate(installment.projectedDate)}
                            onChange={(e) => handleInstallmentChange(index, 'projectedDate', e.target.value)}
                        />
                    ) : (
                        formatDate(installment.projectedDate)
                    )}
                </td>
                {!previewMode && (<>
                    <td className="p-2 border border-gray-300">
                        {isEditing ? (
                            <CustomInput
                                type="date" size="sm" max={today}
                                value={getInputValueDate(installment.receivedDate)}
                                onChange={(e) => handleInstallmentChange(index, 'receivedDate', e.target.value)}
                            />
                        ) : (
                            formatDate(installment.receivedDate)
                        )}
                    </td>

                    {renderDocCell(installment, 'invoice')}
                    {renderDocCell(installment, 'receipt')}
                </>)}


            </>
        )
    }

    return (
        <div className="w-full overflow-scroll mt-4">
            {/* <div className="flex justify-between items-center p-4 border-b border-blue-gray-100">
                <PrimaryButton onClick={addInstallmentRow} disabled={editingRowIndex === installments.length || isLoadingOverlayVisible}>
                    Add Installment
                </PrimaryButton>
            </div> */}
            {installments.length > 0 || newInstallment ? (
                <table className="bg-white w-full min-w-max table-auto text-left border border-collapse">
                    <thead>
                        <tr>
                            {getTableHead()}
                            {!previewMode && (<th key={"actions"} className="border border-gray-300 bg-blue-gray-50 p-4">
                                <span className="font-normal leading-none opacity-70">
                                    Actions
                                </span>
                            </th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {installments.map((installment, index) => {
                            const isEditing = editingRowIndex === index;
                            const rowKey = installment.subscriptionCustomerInstallmentId || `temp-${index}`;

                            return (
                                <tr key={rowKey} className="even:bg-blue-gray-50/50 hover:bg-blue-gray-50 transition-colors">
                                    {<>{renderTableData(installment, index, isEditing)}
                                        {renderActions(installment, index, isEditing)}</>}

                                </tr>
                            );
                        })}

                        {/* New installment row */}
                        {newInstallment && editingRowIndex === installments.length && (
                            <tr key="new-installment" className="bg-green-50">
                                <td className="p-2 border border-gray-300">{installments.length + 1}</td>
                                
                                <td className="p-2 border border-gray-300">
                                    <CustomInput
                                        type="number" step="0.01" size="sm"
                                        value={newInstallment.projectedAmount ?? ''}
                                        onChange={(e) => handleInstallmentChange(installments.length, 'projectedAmount', e.target.value)}
                                        placeholder="Amount"
                                    />
                                </td>
                                <td className="p-2 border border-gray-300">
                                    <CustomInput
                                        type="number" step="0.01" size="sm"
                                        value={newInstallment.receivedAmount ?? ''}
                                        onChange={(e) => handleInstallmentChange(installments.length, 'receivedAmount', e.target.value)}
                                        placeholder="Amount"
                                    />
                                </td>
                                <td className="p-2 border border-gray-300">
                                    <CustomInput
                                        type="date" size="sm"
                                        value={getInputValueDate(newInstallment.projectedDate)}
                                        onChange={(e) => handleInstallmentChange(installments.length, 'projectedDate', e.target.value)}
                                    />
                                </td>
                                <td className="p-2 border border-gray-300">
                                    <CustomInput
                                        type="date" size="sm" max={today}
                                        value={getInputValueDate(newInstallment.receivedDate)}
                                        onChange={(e) => handleInstallmentChange(installments.length, 'receivedDate', e.target.value)}
                                    />
                                </td>

                                <td className="p-2 border border-gray-300 text-center text-gray-400">—</td>
                                <td className="p-2 border border-gray-300 text-center text-gray-400">—</td>
                                <td className="p-2 border border-gray-300">
                                    <div className='flex gap-2 items-center'>
                                        <PrimaryButton size="sm" className="w-8" onClick={handleSaveNewInstallment} disabled={isLoadingOverlayVisible}>
                                            {isLoadingOverlayVisible ? <ThreeDots height="18" width="18" color="#fff" /> : 'Save'}
                                        </PrimaryButton>
                                        <OutlineButton size="sm" className="w-8" variant="outline" onClick={cancelAddRow} disabled={isLoadingOverlayVisible}>
                                            Cancel
                                        </OutlineButton>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            ) : (
                <div className="p-4 text-center text-gray-500">
                    No installments found.
                </div>
            )}
        </div>
    );
};

export default InstallmentsTable;